/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * Sağ tık → "Tüm notları göster" ile açılır; kullanım geçmişi API'den çekilir.
 */

import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Box,
  Spinner,
  Center,
  useColorModeValue,
} from "@chakra-ui/react";
import api from "utils/api";

function formatTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(ts);
  }
}

export default function SimNotlarModal({ sim, isOpen, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const borderCol = useColorModeValue("gray.200", "whiteAlpha.200");
  const muted = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    if (!isOpen || !sim?.id) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLogs([]);

    api
      .get(`/api/sim-kartlari/${sim.id}/logs`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) {
          setError("Notlar okunamadı.");
          return;
        }
        const sorted = [...data].sort((a, b) => {
          const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return tb - ta;
        });
        setLogs(sorted);
      })
      .catch(() => {
        if (!cancelled) setError("Notlar yüklenirken bir hata oluştu.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, sim?.id]);

  const titleTel = sim?.tel_no || "—";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent maxH="85vh">
        <ModalHeader pb={1}>
          <Text fontSize="lg" fontWeight="semibold">
            Tüm notlar
          </Text>
          <Text fontSize="sm" fontWeight="normal" color={muted} mt={1}>
            Telefon: <Text as="span" fontWeight="medium">{titleTel}</Text>
            {sim?.iccid ? (
              <>
                {" · "}
                ICCID: {sim.iccid}
              </>
            ) : null}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={2}>
          {loading ? (
            <Center py={12}>
              <Spinner thickness="3px" color="blue.500" size="lg" />
            </Center>
          ) : error ? (
            <Center py={10}>
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            </Center>
          ) : logs.length === 0 ? (
            <Center py={10}>
              <Text fontSize="sm" color={muted}>
                Bu SIM için kayıtlı not bulunmuyor.
              </Text>
            </Center>
          ) : (
            <VStack align="stretch" spacing={3} pb={2}>
              {logs.map((log) => (
                <Box
                  key={log.id}
                  borderWidth="1px"
                  borderColor={borderCol}
                  borderRadius="lg"
                  bg={cardBg}
                  px={4}
                  py={3}
                >
                  <Text fontSize="xs" color={muted} letterSpacing="wide">
                    {formatTs(log.timestamp)}
                  </Text>
                  <Text fontSize="xs" color={muted} mt={0.5}>
                    Kayıt tel: {log.tel_no || "—"}
                  </Text>
                  <Text
                    fontSize="sm"
                    mt={2}
                    lineHeight="tall"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {log.note?.trim() ? log.note : (
                      <Text as="span" fontStyle="italic" color={muted}>
                        (not yok)
                      </Text>
                    )}
                  </Text>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor={borderCol}>
          <Button onClick={onClose} colorScheme="blue" variant="solid">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
